import axiosInstance from '../../../axiosInstance';
import apiEndpoints from '../../../api';
import { notify, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from '../../../../utils/notification';

// Surface the backend's message on error. Covers BOTH envelope shapes:
//  - business errors  { success:false, message }
//  - validation (400) { status:"error", message }
const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const clean = (obj) => {
    const out = {};
    Object.keys(obj || {}).forEach((k) => {
        const v = obj[k];
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

// ---------- Society dropdown ----------

export const getSocietyDropdownReq = (action) => {
    const params = clean({ search: action.data?.search });
    return axiosInstance.get(apiEndpoints.CTRL_SOCIETY_DROPDOWN_API, { params })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load communities')); return err; });
};

// ---------- Employees ----------

// Shared filter params for the flat list and the grouped directory.
const listParams = (d = {}) => clean({
    page: d.page || 1,
    limit: d.limit || 10,
    search: d.search,
    society_id: d.society_id,
    user_id: d.user_id,
    employee_type_id: d.employee_type_id,
    department: d.department,
    employment_type: d.employment_type,
    is_active: d.is_active,
});

export const getEmployeesListReq = (action) => {
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_LIST_API, { params: listParams(action.data) })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load employees')); return err; });
};

export const getEmployeeDirectoryReq = (action) => {
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_DIRECTORY_API, { params: listParams(action.data) })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load directory')); return err; });
};

export const getEmployeeSocietiesReq = (action) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_SOCIETIES_API.replace(':id', action.data.id);
    return axiosInstance.get(url)
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load societies')); return err; });
};

export const assignEmployeeSocietyReq = (action) => {
    const { id, onSuccess, onError, ...payload } = action.data || {};
    const url = apiEndpoints.CTRL_EMPLOYEE_SOCIETIES_API.replace(':id', id);
    return axiosInstance.post(url, clean(payload))
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Added to community successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to add to community')); return err; });
};

export const changeEmployeeSocietyRoleReq = (action) => {
    const { id, societyId, onSuccess, onError, ...payload } = action.data || {};
    const url = apiEndpoints.CTRL_EMPLOYEE_SOCIETY_ROLE_API
        .replace(':id', id)
        .replace(':societyId', societyId);
    return axiosInstance.patch(url, clean(payload))
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Role updated successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to update role')); return err; });
};

export const getEmployeeStatsReq = (action) => {
    const params = clean({ society_id: action.data?.society_id });
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_STATS_API, { params })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load statistics')); return err; });
};

export const getEmployeeDetailsReq = (action) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_DETAILS_API.replace(':id', action.data.id);
    return axiosInstance.get(url)
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load employee')); return err; });
};

export const createEmployeeReq = (action) => {
    const { onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_CREATE_API, clean(payload))
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee created successfully');
            return res;
        })
        .catch((err) => {
            notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to create employee'));
            return err;
        });
};

export const updateEmployeeReq = (action) => {
    const { id, onSuccess, onError, ...payload } = action.data || {};
    const url = apiEndpoints.CTRL_EMPLOYEE_UPDATE_API.replace(':id', id);
    // Update is partial: `null` means "clear this field", so only drop
    // undefined keys here — clean() would also drop the nulls.
    const body = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
    return axiosInstance.put(url, body)
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee updated successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to update employee')); return err; });
};

export const deleteEmployeeReq = (action) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_DELETE_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee deleted successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to delete employee')); return err; });
};

// PUT /api/employees/:id/password — admin sets a new login password (no current
// password needed). The body only takes the two password fields; the society goes
// in X-Society-Id and must be the employee's own. Logs the employee out everywhere.
// Called directly by the modal (no saga); throws with a readable message on failure.
export const changeEmployeePasswordReq = ({ employeeId, societyId, newPassword, confirmPassword }) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_CHANGE_PASSWORD_API.replace(':id', employeeId);
    const headers = societyId != null ? { 'X-Society-Id': societyId } : {};
    return axiosInstance.put(url, { new_password: newPassword, confirm_password: confirmPassword }, { headers })
        .catch((err) => {
            throw new Error(String(errMsg(err, 'Failed to change password')).replace(/^body:\s*/, ''));
        });
};

export const bulkStatusEmployeeReq = (action) => {
    const { employee_ids, is_active } = action.data || {};
    return axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_BULK_STATUS_API, { employee_ids, is_active })
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employees updated successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Bulk update failed')); return err; });
};

export const bulkReassignEmployeeReq = (action) => {
    const { employee_ids, society_id } = action.data || {};
    return axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_BULK_REASSIGN_API, { employee_ids, society_id })
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employees reassigned successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Bulk reassign failed')); return err; });
};

export const searchEmployeeByEmailReq = (action) => {
    const params = clean({ email: action.data?.email, society_id: action.data?.society_id });
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_SEARCH_BY_EMAIL_API, { params })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Employee not found')); return err; });
};

export const linkExistingEmployeeReq = (action) => {
    const { onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_LINK_EXISTING_API, clean(payload))
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee linked to society');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to link employee')); return err; });
};

export const getEmployeeTypesDropdownReq = () => {
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_ROLES_DROPDOWN_API)
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load employee types')); return err; });
};

// ---------- Employee Types (roles) catalog ----------

export const getEmployeeRolesReq = (action) => {
    const d = action.data || {};
    const params = clean({
        page: d.page || 1,
        limit: d.limit || 10,
        search: d.search,
        status: d.status,
    });
    return axiosInstance.get(apiEndpoints.CTRL_EMPLOYEE_ROLES_LIST_API, { params })
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load employee types')); return err; });
};

export const getEmployeeRoleDetailsReq = (action) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_ROLE_DETAILS_API.replace(':id', action.data.id);
    return axiosInstance.get(url)
        .then(res => res)
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to load employee type')); return err; });
};

export const createEmployeeRoleReq = (action) => {
    const { onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_ROLE_CREATE_API, payload)
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee type created successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to create employee type')); return err; });
};

export const updateEmployeeRoleReq = (action) => {
    const { id, onSuccess, onError, ...payload } = action.data || {};
    const url = apiEndpoints.CTRL_EMPLOYEE_ROLE_UPDATE_API.replace(':id', id);
    return axiosInstance.put(url, payload)
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee type updated successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to update employee type')); return err; });
};

export const deleteEmployeeRoleReq = (action) => {
    const url = apiEndpoints.CTRL_EMPLOYEE_ROLE_DELETE_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then((res) => {
            if (res?.success) notify(NOTIFICATION_TYPE_SUCCESS, res.message || 'Employee type deleted successfully');
            return res;
        })
        .catch((err) => { notify(NOTIFICATION_TYPE_ERROR, errMsg(err, 'Failed to delete employee type')); return err; });
};
